import { vec2 } from "@tlaukkan/tsm";
import { Entity, System } from "ecsy";
import { passVec2 } from "../../shared/box2dUtils";
import { Consumer, Dead, ElementInfo, Magnet, TaggedVirus, Targeting, TargetingSpec, Transform, VisibleRange } from "../../shared/components";
import { CollisionCategory } from "../../shared/constants";
import { Game } from "../../shared/game";
import { GameElement } from "../../shared/gameElementTypes";
import { PhysicsSystem } from "../../shared/systems/physicsSystem";

export type EntitySearchResult = {
	categoryFlags: number,
	categories: Array<CollisionCategory>,
	entities: Array<Entity>,
}

const totalSize = 1000;
const cellSize = 25;
const axisDivisions = totalSize / cellSize;
const cellHalfSize = cellSize / 2;
const cellDiagonal = Math.sqrt(2 * Math.pow(cellSize, 2));
const cellHalfDiagonal = cellDiagonal / 2;

export class TargetingSystem extends System {
	game: Game;
	protected searchGridCellCount = axisDivisions * axisDivisions;
	protected searchCache = new Array<EntitySearchResult>(this.searchGridCellCount);

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	init() {
		TargetingSystem._instance = this;
	}

	private static _instance: TargetingSystem;

	static instance() {
		return TargetingSystem.instance;
	}

	//TODO: Factor search grid stuff into utility lib
	getBoxCenter(index: number, dest?: vec2) {
		const offsetLength = totalSize / 2;
		const x = ((index % axisDivisions) * cellSize) - offsetLength;
		const y = ((Math.floor(index / axisDivisions)) * cellSize) - offsetLength;
		const result = dest || new vec2();
		result.xy = [x + (cellSize / 2), y + (cellSize / 2)];
		return result;
	}

	//Helper functions for grid
	computeSearchCacheIndex(pos: vec2) {
		const offsetLength = totalSize / 2;
		const x = Math.floor((pos.x + offsetLength) / cellSize);
		const y = Math.floor((pos.y + offsetLength) / cellSize);
		const index = x + (y * axisDivisions);
		return index;
	}

	getNeighborIndices(index: number): [number, number, number, number] {
		const result = [];
		if (index > 0) result.push(index - 1);
		if (index < this.searchGridCellCount - 1) result.push(index + 1);
		if (index >= axisDivisions) result.push(index - axisDivisions);
		if (index < this.searchGridCellCount - axisDivisions) result.push(index + axisDivisions);
		return result as [number, number, number, number];
	}

	isRectInBounds(rectCenter: vec2, width: number, xmin: number, ymin: number, xmax: number, ymax: number) {
		// Calculate the coordinates of the square's corners
		const halfWidth = width / 2;
		const left = rectCenter.x - halfWidth;
		const right = rectCenter.x + halfWidth;
		const top = rectCenter.y - halfWidth;
		const bottom = rectCenter.y + halfWidth;

		// Check if the square is within the rectangular bounds
		return !(left > xmax || right < xmin || top > ymax || bottom < ymin);
	}


	getCellsInRadius(pos: vec2, radius: number) {
		const boundXMin = pos.x - radius;
		const boundXMax = pos.x + radius;
		const boundYMin = pos.y - radius;
		const boundYMax = pos.y + radius;
		let results = [this.computeSearchCacheIndex(pos)];
		let visited = new Set<number>();
		let checked = 0;
		while (checked < results.length) {
			const current = results[checked];
			visited.add(current);
			this.getNeighborIndices(current).forEach(cell => {
				if (visited.has(cell)) return;
				const center = this.getBoxCenter(cell);
				if (!this.isRectInBounds(center, cellSize, boundXMin, boundYMin, boundXMax, boundYMax)) return;
				results.push(cell);
			});
			++checked;
		}
		return results;
	}

	execute() {
		this.searchCache = new Array<EntitySearchResult>(this.searchGridCellCount);
	}

	findAllTargets(
		searchCache: EntitySearchResult[],
		entity: Entity,
		spec: TargetingSpec,
		excludeList?: Set<number>
	) {
		let results = new Array<Entity>();
		const excludeTagged = spec.taggedRange < 0;
		if (spec.range > 0) {
			results = results.concat(this.searchForTargets(searchCache, entity, spec.range, spec.forwardOffset, spec.filter, false, false, excludeTagged, excludeList, undefined, spec.excludeElements));
		}
		if (spec.taggedRange > spec.range) {
			results = results.concat(this.searchForTargets(searchCache, entity, spec.taggedRange, spec.forwardOffset, spec.filter, false, true, excludeTagged, excludeList, undefined, spec.excludeElements));
		}

		return results;
	}


	findNearestTarget(
		searchCache: EntitySearchResult[],
		entity: Entity,
		spec: TargetingSpec,
		prevTarget: Entity | null,
		excludeList?: Set<number>,
		resetExcludeList?: boolean
	) {
		const excludeTagged = spec.taggedRange < 0;

		let range = spec.range;

		// Find distance to last valid target. If sticky and in range we can drop this, otherwise we can reduce search range to the nearest still valid target
		// TODO: This optimization should not kick in if prev target is in the exclude list
		if (prevTarget !== null && !this.isUntargetable(prevTarget) && !excludeList) {
			const nearestPosition = prevTarget.getComponent(Transform).position;
			const distanceToNearest = vec2.distance(entity.getComponent(Transform).position, nearestPosition);
			if (distanceToNearest < range) {
				if (spec.sticky) { return prevTarget }
				range = distanceToNearest;
			}
		}

		let results: Entity[] = [];

		if (spec.range > 0) {
			results = this.searchForTargets(searchCache, entity, range, spec.forwardOffset, spec.filter, true, false, excludeTagged, excludeList, resetExcludeList, spec.excludeElements);
		}
		if (results.length == 0 && spec.taggedRange > spec.range) {
			results = this.searchForTargets(searchCache, entity, spec.taggedRange, spec.forwardOffset, spec.filter, false, true, excludeTagged, excludeList, resetExcludeList, spec.excludeElements);
		}

		return results.length > 0 ? results[0] : null;
	}

	findLineOfSight(entity: Entity, spec: TargetingSpec) {
		const transform = entity.getComponent(Transform);
		const range = spec.range;
		const searchResults = this.findInLine(transform.position, transform.position.copy().add(transform.forward.copy().scale(range || 50)), spec.nearestOnly);
		let results = this.filterByCategory(searchResults, spec.filter);
		if (spec.excludeElements !== null) {
			const excluded = spec.excludeElements;
			results = results.filter(candidate => {
				const type = candidate.getComponent(ElementInfo)?.type;
				return type === undefined || !excluded.includes(type);
			});
		}
		return results;
	}

	searchForTargets(
		searchCache: Array<EntitySearchResult>,
		entity: Entity,
		range: number,
		forwardOffset: number,
		categoryFilter: CollisionCategory,
		onlyNearest: boolean,
		onlyTagged: boolean,
		excludeTagged = false,
		excludeList?: Set<number>,
		resetExcludeList?: boolean,
		excludeElements: GameElement[] | null = null,
	) {
		const transform = entity.getComponent(Transform);
		const visited = new Set<number>();
		let results = new Array<Entity>();
		let resultsSet = new Set<number>();
		let maxRange = range;
		let startingPos = transform.position;
		if (forwardOffset !== 0) {
			startingPos = startingPos.copy().add(transform.forward.copy().scale(forwardOffset));
		}

		//Breadth first search of neigboring grid squares, starting from current one
		const startingIndex = this.computeSearchCacheIndex(startingPos);
		const startingCellCenter = this.getBoxCenter(startingIndex);
		const queue = Array<{ center: vec2, distance: number, index: number }>();
		let queueIndex = 0;
		let iter = 0;
		queue.push({ center: startingCellCenter, distance: 0, index: startingIndex });
		while (queue.length > queueIndex) {
			//Discard out of range cells
			const cell = queue[queueIndex++];
			const boxInsideRange = (cell.distance - cellHalfDiagonal) <= maxRange;
			if (!boxInsideRange) continue;

			//Get the entities in candidate cells, memoizing cells as we go
			//TODO: maybe can go outside max range of cache since surrounding we dont check for it

			let searchResult = searchCache[cell.index];
			if (!searchResult) {
				searchResult = this.findInRange(cell.center, PhysicsSystem.world, cellHalfSize);
				searchCache[cell.index] = searchResult;
			}
			let candidates = this.filterByCategory(searchResult, categoryFilter);
			if (onlyTagged) {
				candidates = candidates.filter(candidate => candidate.hasComponent(TaggedVirus));
			}
			if (excludeTagged) {
				candidates = candidates.filter(candidate => !candidate.hasComponent(TaggedVirus));
			}
			if (excludeList) {
				candidates = candidates.filter(candidate => !excludeList.has(candidate.id));
			}
			if (excludeElements !== null) {
				candidates = candidates.filter(candidate => {
					const type = candidate.getComponent(ElementInfo)?.type;
					return type === undefined || !excludeElements.includes(type);
				});
			}
			if (onlyNearest) {
				//Find the nearest. If we found one we can contrain the search
				const nearestResult = this.chooseNearest(entity, candidates, startingPos);
				if (nearestResult) {
					const nearestPosition = nearestResult.getComponent(Transform).position;
					const distanceToNearest = vec2.distance(startingPos, nearestPosition);
					if (distanceToNearest <= maxRange) {
						maxRange = distanceToNearest;
						results = [nearestResult];
					}
				}
			}
			else {
				for (const hit of candidates) {
					if (resultsSet.has(hit.id)) continue;
					const hitPos = hit.getComponent(Transform).position;
					//Must check if inside circular radius
					if (vec2.distance(hitPos, startingPos) < maxRange) {
						results.push(hit);
						resultsSet.add(hit.id);
					}
				}
			}

			//Can early out no other cells in range
			//Opt: Could probably rule out some neighbors as well based on overlap, even if some fit
			const cellRelativePos = vec2.distance(cell.center, startingPos);
			if (cellRelativePos + maxRange < cellHalfSize) continue;

			//Check neighbors if they are within range
			const neighbors = this.getNeighborIndices(cell.index);
			for (const neighbor of neighbors) { //4 cardinal neighbors
				if (!visited.has(neighbor)) {
					const neighborCellCenter = this.getBoxCenter(neighbor);
					const distanceFromSearcher = vec2.distance(startingPos, neighborCellCenter);
					const boxInsideRange = (distanceFromSearcher - cellHalfDiagonal) <= maxRange;
					const boxNotBacktracking = distanceFromSearcher > cell.distance;
					const shouldCheckBox = boxNotBacktracking && boxInsideRange
					if (shouldCheckBox) {
						queue.push({ center: neighborCellCenter, distance: distanceFromSearcher, index: neighbor });
						visited.add(neighbor);
					}
				}
			}
			//Testing code
			// const check = findClosestInRange(transform.position, physics, targeting.range, e => e !== entity && e.hasComponent(Virus));
			// if (check && results.length === 0) failCount++;
			// if (!check && results.length > 0) failCount++;
			// if (check && results[0] && check.id !== results[0].id) failCount++;
		}

		if (excludeList) {
			// number of candidates and number of filtered candidates
			if (results.length === 0 && resetExcludeList) {
				excludeList.clear();
			}
		}

		return results;
	}

	findInRange(position: vec2, world: B2D.b2World, range: number) {
		const results: EntitySearchResult = {
			categoryFlags: 0,
			entities: [],
			categories: []
		}

		const aabb = new B2D.b2AABB();
		aabb.set_lowerBound(passVec2(new vec2([position.x - range, position.y - range])));
		aabb.set_upperBound(passVec2(new vec2([position.x + range, position.y + range])));

		const aabbCallback = new B2D.JSQueryCallback();
		aabbCallback.ReportFixture = (fixturePtr) => {
			const fixture = B2D.wrapPointer(fixturePtr, B2D.b2Fixture);
			const filter = fixture.GetFilterData();
			const categoryBits = filter.get_categoryBits();

			const target = PhysicsSystem.fixtureToEntityMap.get(fixture);
			if (target) {
				results.categoryFlags = results.categoryFlags | categoryBits;
				results.categories.push(categoryBits);
				results.entities.push(target);
			}
			return true; //continue looping
		};

		world.QueryAABB(aabbCallback, aabb);

		B2D.destroy(aabb);
		B2D.destroy(aabbCallback);

		return results;
	}

	findInLine(start: vec2, end: vec2, nearestOnly = false) {
		const results: EntitySearchResult = {
			categoryFlags: 0,
			entities: [],
			categories: []
		}

		//Box2D reports hits in arbitrary order, not by distance, so collect them with their fraction along the ray and sort
		const hits = new Array<{ entity: Entity, category: number, fraction: number }>();
		const callback = new B2D.JSRayCastCallback();
		callback.ReportFixture = (fixturePtr, point, normal, fraction) => {
			const fixture = B2D.wrapPointer(fixturePtr, B2D.b2Fixture);
			const filter = fixture.GetFilterData();
			const categoryBits = filter.get_categoryBits();

			const target = PhysicsSystem.fixtureToEntityMap.get(fixture);
			if (target) {
				hits.push({ entity: target, category: categoryBits, fraction });
			}

			//Returning the fraction clips the ray to this hit, so later reports can only be nearer
			return nearestOnly ? fraction : 1;
		}
		PhysicsSystem.world.RayCast(callback, passVec2(start), passVec2(end));

		hits.sort((a, b) => a.fraction - b.fraction);
		if (nearestOnly) hits.splice(1);
		for (const hit of hits) {
			results.categoryFlags = results.categoryFlags | hit.category;
			results.categories.push(hit.category);
			results.entities.push(hit.entity);
		}
		return results;
	}

	filterByCategory(searchHits: EntitySearchResult, category: CollisionCategory | -1) {
		if (category === -1) {
			return [...searchHits.entities];
		}
		const results = new Array<Entity>();
		const hasCategory = (searchHits.categoryFlags & category)
		if (hasCategory > 0) {
			for (let i = 0; i < searchHits.categories.length; ++i) {
				const candidateCategory = searchHits.categories[i];
				if (candidateCategory & category) {
					results.push(searchHits.entities[i]);
				}
			}
		}
		return results;
	}

	chooseNearest(entity: Entity, candidates: Entity[], position?: vec2) {
		if (!position) position = entity.getComponent(Transform).position;
		let nearest: Entity | null = null;
		let nearestSqrDistance = Infinity;
		for (const target of candidates) {
			if (target == entity) continue;
			if (!target) continue;
			if (this.isUntargetable(entity)) continue;
			const targetTransform = target.getComponent(Transform);
			if (targetTransform) {
				const sqrDistance = targetTransform.position.copy().subtract(position).squaredLength()
				if (sqrDistance < nearestSqrDistance) {
					nearestSqrDistance = sqrDistance;
					nearest = target;
				}
			}
		}
		return nearest;
	}

	isUntargetable(entity: Entity) {
		return entity.hasComponent(Dead) || !entity.hasComponent(Transform);
	}
}